import { ApolloClient } from "@apollo/client";
import { from, split, ApolloLink } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
// import { RetryLink } from "@apollo/client/link/retry";
import { createUploadLink } from "apollo-upload-client";
import fetch from "node-fetch";
import { WebSocketLink } from "@apollo/client/link/ws";
import { SubscriptionClient } from "subscriptions-transport-ws";
import { getMainDefinition } from "@apollo/client/utilities";
import getReferrer from "core/utils/getReferrer";
import apm from "./apm";
import LocalStorage from "./localstorage";
import createApolloCache from "core/utils/createApolloCache";
import { isTelegramMiniApp, launchParams } from "core/utils/telegram";
import {
  isMobileAppClient,
  MOBILE_APP_HEADER,
  MOBILE_APP_PLATFORM,
} from "core/utils/mobileApp";

// import i18n from "core/utils/i18n";

const ls = new LocalStorage(`apollo`);

let viewerId = ls.getItem("viewerId");
let authToken = ls.getItem("authToken");

const httpLink = createUploadLink({
  uri: "/site/graphql",
  fetch,
  credentials: "same-origin",
  // useGETForQueries: true,
});

const auth = new ApolloLink((operation, forward) => {
  const url = getReferrer();

  const headers = {
    "X-Referer": url,
    "Apollo-Require-Preflight": "true",
  };

  if (launchParams) {
    headers["X-MiniApp-Authorization"] = launchParams.initDataRaw;
  }

  if (viewerId) {
    headers["x-viewerId"] = viewerId;
  }

  if (isMobileAppClient()) {
    headers[MOBILE_APP_HEADER] = MOBILE_APP_PLATFORM;
  }

  operation.setContext({ headers });

  const result = forward(operation);

  if (!result.map) return result;

  return result.map((response) => {
    const context = operation.getContext();
    if (viewerId !== context.response.headers.get("x-viewerId")) {
      viewerId = context.response.headers.get("x-viewerId");
      ls.setItem("viewerId", viewerId);
    }

    const newAuthToken = context.response.headers.get("x-auth-token");
    if (newAuthToken && authToken !== newAuthToken) {
      authToken = newAuthToken;
      ls.setItem("authToken", authToken);

      // Переконнект WS с новым токеном
      wsLink?.subscriptionClient?.client?.close();
    }
    return response;
  });
});

// const retryLink = new RetryLink();

let errorsCount = 0;

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.map((error) => {
      console.log(
        `[GraphQL error]: Code: ${
          error.extensions?.code || "Unknown"
        } Message: ${error.message}`
      );

      apm.captureError(error);
    });

  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
    apm.captureError(networkError);
  }

  errorsCount++;

  if (typeof window !== "undefined") {
    if (errorsCount > 100) {
      console.log("force reload");
      window.location.reload(true);
    }
  }
});

const apolloCache = createApolloCache();
if (typeof window !== "undefined") {
  apolloCache.restore(window.__APOLLO_STATE__);
}

const isPolkasoftDomain =
  typeof window !== "undefined" &&
  window.location.hostname.endsWith("polkasoft.shop");

const wsUri =
  isPolkasoftDomain && process.env.RAZZLE_GRAPHQL_POLKASOFT_SUBSCRIPTION_URL
    ? process.env.RAZZLE_GRAPHQL_POLKASOFT_SUBSCRIPTION_URL
    : isTelegramMiniApp && process.env.RAZZLE_GRAPHQL_TELEGRAM_SUBSCRIPTION_URL
    ? process.env.RAZZLE_GRAPHQL_TELEGRAM_SUBSCRIPTION_URL
    : process.env.RAZZLE_GRAPHQL_SUBSCRIPTION_URL;

export const wsClient = process.browser
  ? new SubscriptionClient(wsUri, {
      lazy: true,
      reconnect: true,
      connectionParams: () => {
        const params = {};
        const referer = getReferrer();

        if (authToken) {
          params.Authorization = authToken;
        }

        if (viewerId) {
          params.viewerId = viewerId;
        }

        params.host = window.location.hostname;
        params.referer = referer;

        return params;
      },
    })
  : null;

export const wsLink = wsClient ? new WebSocketLink(wsClient) : null;

/**
 * Для снижения нагрузки на апи
 * часть запросов будем отсылать по вебсокетам
 * если установлено соединение
 * запросы wsMutations и wsQuery
 *
 * Надо помнить что в эти треки не прокидывается реферер
 */
const wsMutations = [
  "TrackWidget",
  "TrackAd",
  "CreateEvent",
  "UpdateViewData",
  "CreateSponsorImpression",
  "CreateSponsorClick",
  "ChatHeartbeat",
  "ChatLeave",
  // Здесь нету CreateView потому что у нее куча резолверов с даталоадерами
];

const wsQuery = ["NotificationCount"];

const link = process.browser
  ? split(
      ({ query }) => {
        const definition = getMainDefinition(query);

        if (!definition.kind === "OperationDefinition") {
          return false;
        }

        if (definition.operation === "subscription") {
          return true;
        }

        // if (wsLink?.subscriptionClient?.client?.readyState !== 1) {
        //   return false;
        // }

        if (definition.operation === "mutation") {
          return wsMutations.includes(definition.name.value);
        }

        if (definition.operation === "query") {
          return wsQuery.includes(definition.name.value);
        }

        return false;
      },
      wsLink,
      httpLink
    )
  : httpLink;

export const client = new ApolloClient({
  ssrForceFetchDelay: 100,
  ssrMode: typeof window === "undefined",
  link: from([
    // retryLink,
    errorLink,
    auth,
    link,
  ]),
  cache: apolloCache,
  assumeImmutableResults: true,
  connectToDevTools: true,
});
